import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useGuardarPreferencias} from '../api/sesion';
import {tr} from '../i18n/idioma';
import {useUsuario} from './sesion-contexto';

export type RevisionDeEliminacion = {cantidad: number; lineas: string[]};

type Props = {
  etiqueta: string;
  titulo: string;
  deshabilitado?: boolean;
  // Mostrar siempre la revision, aunque se haya pedido no volver a mostrar el
  // aviso (empresas: lo que queda sin empresa hay que verlo siempre).
  siempreRevisar?: boolean;
  // Que se borraria (sin borrar nada) y el borrado; este devuelve el mensaje final.
  revisar: () => Promise<RevisionDeEliminacion>;
  eliminar: () => Promise<string>;
  onTerminado?: () => void;
};

// Boton de eliminar con aviso de seguridad: primero muestra que se borraria y
// pide aprobarlo. "No volver a mostrar en este inicio de sesion" queda en el
// perfil atado al ingreso actual; al entrar de nuevo el aviso vuelve.
export function BotonDeEliminar({
  etiqueta,
  titulo,
  deshabilitado,
  siempreRevisar,
  revisar,
  eliminar,
  onTerminado,
}: Props) {
  const usuario = useUsuario();
  const guardar = useGuardarPreferencias();
  const ingreso = usuario.ultimoIngreso ?? 'sin-ingreso';
  const sinAviso = !siempreRevisar && usuario.preferencias.sinAvisoDeEliminarEn === ingreso;
  const [revision, setRevision] = useState<RevisionDeEliminacion | null>(null);
  const [noVolver, setNoVolver] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);

  const fallo = (error: unknown) =>
    setMensaje({
      texto: error instanceof ErrorDeApi ? error.message : tr('No se pudo eliminar.', 'Could not delete.'),
      error: true,
    });

  const ejecutar = async () => {
    setOcupado(true);
    try {
      if (noVolver) await guardar.mutateAsync({columnas: {}, sinAvisoDeEliminarEn: ingreso}).catch(() => undefined);
      const texto = await eliminar();
      setRevision(null);
      setMensaje({texto, error: false});
      onTerminado?.();
    } catch (error) {
      setRevision(null);
      fallo(error);
    } finally {
      setOcupado(false);
    }
  };

  const empezar = async () => {
    setMensaje(null);
    if (sinAviso) return ejecutar();
    setOcupado(true);
    try {
      setNoVolver(false);
      setRevision(await revisar());
    } catch (error) {
      fallo(error);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => void empezar()}
        disabled={deshabilitado || ocupado}
        title={titulo}
        className="rounded-md border border-alerta/50 bg-white px-2 py-0.5 text-xs text-alerta hover:bg-alerta/10 disabled:opacity-40"
      >
        {etiqueta}
      </button>
      {mensaje && (
        <span role="status" className={`text-xs ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`}>
          {mensaje.texto}
        </span>
      )}
      {revision && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4">
          <section
            role="alertdialog"
            aria-modal="true"
            aria-label={titulo}
            className="w-full max-w-lg rounded-xl border border-alerta/50 bg-white p-5 shadow-2xl"
          >
            <h2 className="text-lg font-semibold text-alerta">⚠ {titulo}</h2>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
              {revision.lineas.map(linea => (
                <li key={linea}>{linea}</li>
              ))}
            </ul>
            {revision.cantidad > 0 ? (
              <>
                <p className="mt-3 text-sm font-medium">
                  {tr(
                    'Queda en la bitácora con todos sus datos. Un administrador lo puede deshacer desde Administración → Bitácora.',
                    'It is kept in the audit log with all its data. An administrator can undo it from Administration → Audit log.',
                  )}
                </p>
                {!siempreRevisar && (
                  <label className="mt-3 flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={noVolver} onChange={e => setNoVolver(e.target.checked)} />
                    {tr('No volver a mostrar en este inicio de sesión', 'Do not show again during this session')}
                  </label>
                )}
              </>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRevision(null)}
                disabled={ocupado}
                className="rounded-md border border-linea bg-white px-4 py-2 text-sm hover:bg-papel"
              >
                {revision.cantidad > 0 ? tr('Cancelar', 'Cancel') : tr('Cerrar', 'Close')}
              </button>
              {revision.cantidad > 0 && (
                <button
                  type="button"
                  onClick={() => void ejecutar()}
                  disabled={ocupado}
                  className="rounded-md bg-alerta px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  {ocupado ? tr('Eliminando…', 'Deleting…') : tr('Sí, eliminar', 'Yes, delete')}
                </button>
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}
